<template>
  <ResponsiveDialog
    v-model="dialogVisible"
    :title="isEdit ? '일정 수정' : '일정 추가'"
    width="560px"
    :before-close="handleClose"
    footer-spread
  >
    <el-form
      ref="formRef"
      class="ed"
      :model="form"
      :rules="rules"
      label-width="0"
      label-position="top"
      @submit.prevent="handleSubmit"
      :disabled="isEdit && !canEdit"
    >
      <!--
        종류가 맨 위로 올라왔다.
        예전에는 폼 맨 아래 el-select 였다 — 폰에서 일정 하나 만들려면 제목·설명·반복·
        종일·시작·종료·위치를 지나야 종류가 나왔고, 그중 반복은 대부분의 일정에 필요 없는
        필드였는데도 두 번째 자리를 차지하고 있었다.
        선택지가 6개면 드롭다운(열기+고르기 = 탭 2회)보다 칩 한 줄(탭 1회)이 빠르다.
      -->
      <!-- role="radiogroup" 을 쓰지 않는다. ARIA 라디오그룹은 그룹 전체가 탭 스톱
           하나이고 방향키로 이동하는 계약인데, 여기는 버튼마다 탭 스톱이고 방향키가
           아무 것도 하지 않는다. 지킬 수 없는 역할은 선언하지 않는 편이 낫다. -->
      <div class="ed-types" role="group" aria-label="일정 종류">
        <button
          v-for="t in eventTypeOptions"
          :key="t.value"
          type="button"
          :aria-pressed="form.eventType === t.value"
          :disabled="isReadonly"
          class="ed-type"
          :class="[`ed-type--${t.value}`, { 'is-active': form.eventType === t.value }]"
          @click="selectEventType(t.value)"
        >{{ t.label }}</button>
      </div>

      <el-form-item prop="title" class="ed-item ed-item--title">
        <el-input
          v-model="form.title"
          class="ed-title"
          placeholder="무엇을 하나요?"
          maxlength="255"
          show-word-limit
        />
      </el-form-item>

      <!-- ────────────── 언제 ────────────── -->
      <div class="ed-card">
        <!--
          날짜와 시간을 분리했다. 예전에는 type="datetime" 픽커 하나가 둘을 같이 받았는데,
          그 픽커는 폰에서 날짜판과 시간판을 한 팝오버에 욱여넣어 시간 열이 화면 밖으로 잘렸다.
          form.startDate/endDate 의 문자열('YYYY-MM-DD HH:mm:ss')은 그대로 두고
          앞 10글자·뒤 8글자만 따로 갈아끼운다(startDatePart / startTimePart).
        -->
        <div v-if="!form.isRecurring || isEdit" class="ed-row ed-row--dt">
          <el-icon class="ed-row__ico"><Calendar /></el-icon>
          <span class="ed-row__label">날짜</span>
          <div class="ed-row__val">
            <el-form-item prop="startDate" class="ed-item ed-item--inline">
              <el-date-picker
                v-model="startDatePart"
                type="date"
                class="ed-date"
                format="M/D(dd)"
                value-format="YYYY-MM-DD"
                placeholder="시작"
                :clearable="false"
                :editable="false"
                @change="revalidateDates"
              />
            </el-form-item>
            <span class="ed-tilde" aria-hidden="true">~</span>
            <el-form-item prop="endDate" class="ed-item ed-item--inline">
              <el-date-picker
                v-model="endDatePart"
                type="date"
                class="ed-date"
                format="M/D(dd)"
                value-format="YYYY-MM-DD"
                placeholder="종료"
                :clearable="false"
                :editable="false"
                @change="revalidateDates"
              />
            </el-form-item>
          </div>
        </div>

        <!-- 종일이면 시간 행은 아예 없다. 00:00~23:59 를 보여줘 봐야 고를 것이 없다. -->
        <div v-if="!form.isAllDay" class="ed-row ed-row--dt">
          <el-icon class="ed-row__ico"><Clock /></el-icon>
          <span class="ed-row__label">시간</span>
          <div class="ed-row__val">
            <template v-if="!form.isRecurring || isEdit">
              <span class="ed-pill">
                <el-time-picker
                  v-model="startTimePart"
                  class="ed-time"
                  format="HH:mm"
                  value-format="HH:mm:ss"
                  placeholder="시작"
                  :clearable="false"
                  :editable="false"
                  @change="revalidateDates"
                />
              </span>
              <span class="ed-tilde" aria-hidden="true">~</span>
              <span class="ed-pill">
                <el-time-picker
                  v-model="endTimePart"
                  class="ed-time"
                  format="HH:mm"
                  value-format="HH:mm:ss"
                  placeholder="종료"
                  :clearable="false"
                  :editable="false"
                  @change="revalidateDates"
                />
              </span>
            </template>
            <!-- 반복 생성 모드는 날짜가 시리즈에서 나오므로 시간만 따로 받는다(기존 동작). -->
            <template v-else>
              <span class="ed-pill">
                <el-time-picker
                  v-model="form.startTime"
                  class="ed-time"
                  format="HH:mm"
                  value-format="HH:mm:ss"
                  placeholder="시작"
                  :clearable="false"
                  :editable="false"
                />
              </span>
              <span class="ed-tilde" aria-hidden="true">~</span>
              <span class="ed-pill">
                <el-time-picker
                  v-model="form.endTime"
                  class="ed-time"
                  format="HH:mm"
                  value-format="HH:mm:ss"
                  placeholder="종료"
                  :clearable="false"
                  :editable="false"
                />
              </span>
            </template>
          </div>
        </div>

        <!--
          반복은 접힌 채가 기본이다. 대부분의 일정은 한 번 있고 끝나는데
          예전 폼은 반복 스위치와 그 하위 5개 필드가 제목 바로 다음에 있었다.
        -->
        <button
          type="button"
          class="ed-row ed-rowbtn"
          :disabled="isReadonly || isEdit || form.isRecurring"
          :aria-expanded="recurringExpanded"
          @click="toggleRecurring"
        >
          <el-icon class="ed-row__ico"><Refresh /></el-icon>
          <span class="ed-row__label">반복</span>
          <span class="ed-row__summary">
            <span :class="{ 'is-muted': !form.isRecurring }">
              {{ isEdit ? '생성 시에만 설정 가능' : recurringSummary }}
            </span>
          </span>
          <el-icon v-if="!isEdit && !form.isRecurring" class="ed-row__chev" :class="{ 'is-open': recurringExpanded }">
            <ArrowDown />
          </el-icon>
        </button>

        <!--
          반복이 켜져 있는 동안에는 이 패널을 접지 않는다(위 버튼이 disabled).
          접으면 el-form-item 이 언마운트되어 '반복 유형/종료일을 고르세요' 검증이
          아예 돌지 않은 채 저장 버튼만 조용히 막히는 상태가 된다.
          되돌리려면 '반복 안 함' 칩을 누르면 되고, 그게 곧 예전의 스위치 off 다.
        -->
        <div v-if="recurringExpanded" class="ed-sub">
          <el-form-item prop="recurringType" class="ed-item">
            <div class="ed-chips" role="group" aria-label="반복 유형">
              <button
                v-for="opt in recurringOptions"
                :key="opt.value || 'none'"
                type="button"
                :aria-pressed="isRecurringTypeActive(opt.value)"
                :disabled="isReadonly"
                class="ed-chip"
                :class="{ 'is-active': isRecurringTypeActive(opt.value) }"
                @click="selectRecurringType(opt.value)"
              >{{ opt.label }}</button>
            </div>
          </el-form-item>

          <template v-if="form.isRecurring">
            <el-form-item v-if="form.recurringType === 'weekly'" prop="recurringDays" class="ed-item">
              <span class="field-label">요일</span>
              <div class="ed-chips ed-chips--days">
                <button
                  v-for="d in weekdays"
                  :key="d.value"
                  type="button"
                  :aria-pressed="isDaySelected(d.value)"
                  :disabled="isReadonly"
                  class="ed-chip ed-chip--day"
                  :class="{ 'is-active': isDaySelected(d.value) }"
                  @click="toggleRecurringDay(d.value)"
                >{{ d.label }}</button>
              </div>
            </el-form-item>

            <el-form-item v-if="form.recurringType === 'monthly'" prop="recurringDayOfMonth" class="ed-item">
              <span class="field-label">매월 며칠</span>
              <el-input-number
                v-model="form.recurringDayOfMonth"
                :min="1"
                :max="31"
                class="ed-num"
              />
            </el-form-item>

            <div class="ed-subgrid">
              <el-form-item prop="recurringStartDate" class="ed-item">
                <span class="field-label">반복 시작</span>
                <el-date-picker
                  v-model="form.recurringStartDate"
                  type="date"
                  format="YYYY-MM-DD"
                  value-format="YYYY-MM-DD"
                  placeholder="시작 날짜"
                  :editable="false"
                />
              </el-form-item>
              <el-form-item prop="recurringEndDate" class="ed-item">
                <span class="field-label">반복 종료</span>
                <el-date-picker
                  v-model="form.recurringEndDate"
                  type="date"
                  format="YYYY-MM-DD"
                  value-format="YYYY-MM-DD"
                  placeholder="종료 날짜"
                  :editable="false"
                />
              </el-form-item>
            </div>
          </template>
        </div>
      </div>

      <!-- ────────────── 종일 · 장소 · 담당 ────────────── -->
      <div class="ed-card">
        <div class="ed-row">
          <el-icon class="ed-row__ico"><Sunny /></el-icon>
          <span class="ed-row__label">종일</span>
          <div class="ed-row__val">
            <el-switch v-model="form.isAllDay" @change="handleAllDayChange" />
          </div>
        </div>

        <!--
          장소는 값이 정해지면 요약 한 줄로 접힌다. 펼쳤을 때 나오는
          [등록 장소 선택] / [직접 입력 + 주소검색] 조합은 그대로다 —
          직접 입력은 지오코딩을 통과해야 저장되기 때문에 검색 버튼을 없앨 수 없다.
        -->
        <button
          type="button"
          class="ed-row ed-rowbtn"
          :disabled="isReadonly"
          :aria-expanded="locationExpanded"
          @click="toggleLocation"
        >
          <el-icon class="ed-row__ico"><Location /></el-icon>
          <span class="ed-row__label">
            장소<span v-if="!locationOptional" class="ed-req" aria-hidden="true">*</span>
          </span>
          <span class="ed-row__summary">
            <span v-if="locationName" class="ed-loc__name u-trunc">{{ locationName }}</span>
            <span v-else class="is-muted">{{ locationOptional ? '입력 안 함' : '장소를 고르세요' }}</span>
            <span v-if="locationAddress" class="ed-loc__addr u-trunc">{{ locationAddress }}</span>
          </span>
          <el-icon class="ed-row__chev" :class="{ 'is-open': locationExpanded }"><ArrowDown /></el-icon>
        </button>

        <div v-if="locationExpanded" class="ed-sub">
          <el-select
            v-model="form.locationId"
            placeholder="등록된 장소에서 고르기"
            filterable
            clearable
            class="ed-full"
            @change="handleLocationSelect"
          >
            <el-option
              v-for="location in locations"
              :key="location.id"
              :label="location.name"
              :value="location.id"
            />
          </el-select>

          <el-input
            v-model="form.locationCustom"
            placeholder="또는 직접 주소를 입력하세요"
            :disabled="!!form.locationId"
            @input="handleLocationInputChange"
          >
            <template #append>
              <el-button
                :icon="Search"
                aria-label="주소 검색"
                @click="searchAddress"
                :disabled="!form.locationCustom || !!form.locationId"
              />
            </template>
          </el-input>

          <p v-if="locationSearched && form.locationCustom" class="ed-note ed-note--ok">
            <el-icon><CircleCheck /></el-icon>
            <span>주소 검색 완료</span>
          </p>
          <p
            v-else-if="form.locationCustom && !locationSearched && !form.locationId && !locationOptional"
            class="ed-note ed-note--warn"
          >주소 검색 버튼을 눌러 주소를 확인해주세요</p>
          <p v-if="locationError" class="ed-note ed-note--err">{{ locationError }}</p>
          <p v-if="locationOptional" class="ed-note">
            휴무·휴가는 장소를 비워도 저장됩니다.
          </p>
        </div>

        <!--
          참석자 목록은 없다 — Event 모델은 소유자 userId 한 명만 갖고, 저장 페이로드에도
          참석자 필드가 없다. 여기 아바타를 여러 개 그리면 실제로는 아무에게도 공유되지
          않는 일정을 공유된 것처럼 보이게 만든다. 그래서 '누구의 일정인지'만 보여준다.
        -->
        <div class="ed-row">
          <el-icon class="ed-row__ico"><User /></el-icon>
          <span class="ed-row__label">담당</span>
          <div class="ed-row__val ed-owner">
            <span class="ed-avatar" aria-hidden="true">
              <img v-if="ownerAvatar" :src="ownerAvatar" alt="" @error="onAvatarError" />
              <span v-else>{{ ownerInitial }}</span>
            </span>
            <span class="ed-owner__name u-trunc">{{ ownerName }}</span>
          </div>
        </div>
      </div>

      <!-- ────────────── 자세히 ────────────── -->
      <div class="ed-card">
        <button
          type="button"
          class="ed-row ed-rowbtn"
          :aria-expanded="detailsOpen"
          @click="detailsOpen = !detailsOpen"
        >
          <el-icon class="ed-row__ico"><Document /></el-icon>
          <span class="ed-row__label">자세히</span>
          <span class="ed-row__summary">
            <span v-if="form.description" class="u-trunc">{{ form.description }}</span>
            <span v-else class="is-muted">설명 없음</span>
          </span>
          <el-icon class="ed-row__chev" :class="{ 'is-open': detailsOpen }"><ArrowDown /></el-icon>
        </button>

        <div v-if="detailsOpen" class="ed-sub">
          <el-form-item prop="description" class="ed-item">
            <span class="field-label">설명</span>
            <el-input
              v-model="form.description"
              type="textarea"
              :rows="4"
              placeholder="메모, 준비물, 참고 링크"
              maxlength="1000"
              show-word-limit
            />
          </el-form-item>
          <p class="ed-note">
            캘린더에 찍히는 색은 위에서 고른 종류를 따라갑니다.
          </p>
        </div>
      </div>
    </el-form>

    <!-- 권한 없음 안내 -->
    <div v-if="isEdit && !canEdit" class="ed-nopermit">
      <el-alert
        title="수정 권한이 없습니다"
        description="본인이 등록한 일정만 수정할 수 있습니다."
        type="warning"
        :closable="false"
      />
    </div>

    <template #footer>
      <!--
        모바일에서는 주 버튼이 풀폭 52px 로 엄지 반경에 온다. '취소' 는 빼는 게 맞다 —
        시트에는 이미 손잡이와 X 가 있고, 폭을 반씩 나눠 갖는 순간 저장이 44px 아래로 좁아진다.
        삭제는 되돌릴 수 없어서 스와이프가 아니라 명시적 버튼으로 왼쪽 끝에 둔다.
      -->
      <div class="ed-footer">
        <el-button
          v-if="isEdit && canEdit"
          class="ed-del"
          type="danger"
          plain
          :loading="loading"
          aria-label="일정 삭제"
          @click="handleDelete"
        >
          <el-icon><Delete /></el-icon>
          <span class="ed-del__text">삭제</span>
        </el-button>
        <div class="ed-footer__main">
          <el-button v-if="!isMobile || !canEdit" @click="handleClose">
            {{ isEdit && !canEdit ? '닫기' : '취소' }}
          </el-button>
          <el-button
            v-if="canEdit"
            type="primary"
            @click="handleSubmit"
            :loading="loading"
          >저장</el-button>
        </div>
      </div>
    </template>
  </ResponsiveDialog>

  <!-- 반복 일정일 때만 열린다. 저장/삭제의 확인 단계를 겸한다. -->
  <EventScopeDialog
    v-model="scopeSheetVisible"
    :mode="scopeMode"
    :date="form.startDate"
    @confirm="handleScopeConfirm"
  />
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  Search, Delete, CircleCheck, Calendar, Clock, Refresh,
  Sunny, Location, User, Document, ArrowDown
} from '@element-plus/icons-vue'
import http from '@/utils/http'
import ResponsiveDialog from '@/components/dialogs/ResponsiveDialog.vue'
import EventScopeDialog from '@/components/agenda/EventScopeDialog.vue'
import { useBreakpoint } from '@/composables/useBreakpoint'
import { getProfileImageUrl, getProfileInitial } from '@/utils/profileUtils'
import {
  EVENT_TYPE_LIST,
  getEventTypeColor
} from '@/utils/eventTypes'
import { toServerDateTime } from '@/utils/agendaItem'

/* 반복 유형 칩. '반복 안 함'(value '')이 예전 스위치의 off 자리다 —
   스위치 + 유형 select 두 컨트롤을 하나로 합쳤다. */
const RECURRING_OPTIONS = [
  { value: '', label: '반복 안 함' },
  { value: 'daily', label: '매일' },
  { value: 'weekly', label: '매주' },
  { value: 'monthly', label: '매월' }
]

/* form.recurringDays 는 el-checkbox 시절부터 **문자열** 배열이었다('0'~'6').
   서버가 그 형태를 받으므로 칩으로 바꿔도 문자열을 유지한다. */
const WEEKDAYS = [
  { value: '0', label: '일' },
  { value: '1', label: '월' },
  { value: '2', label: '화' },
  { value: '3', label: '수' },
  { value: '4', label: '목' },
  { value: '5', label: '금' },
  { value: '6', label: '토' }
]

/* 'YYYY-MM-DD HH:mm:ss' 를 날짜/시간으로 자르고 붙이는 것뿐이다.
   저장 페이로드도, 검증기도, handleAllDayChange 도 이 한 덩어리 문자열만 보므로
   여기서 형식이 어긋나면 조용히 Invalid Date 가 된다. */
function datePartOf(value) {
  return value ? String(value).slice(0, 10) : ''
}
function timePartOf(value) {
  const s = String(value || '')
  return s.length >= 19 ? s.slice(11, 19) : ''
}
function joinDateTime(datePart, timePart) {
  return `${datePart} ${timePart}`
}

export default {
  name: 'EventDialog',
  components: {
    ResponsiveDialog, EventScopeDialog,
    Calendar, Clock, Refresh, Sunny, Location, User, Document, ArrowDown,
    Delete, CircleCheck
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    event: {
      type: Object,
      default: null
    },
    defaultDate: {
      type: Date,
      default: () => new Date()
    },
    defaultEndDate: {
      type: Date,
      default: null
    },
    /*
     * 새 일정의 기본 '종일' 여부.
     *
     * 기본값이 true 인 이유: FAB·일자 시트에서 만들 때는 날짜만 정해져 있고
     * 시각은 아직 모른다. 그때 09:00~10:00 같은 임의의 한 시간을 박아 두면
     * 사용자가 매번 지워야 한다.
     *
     * 반대로 주/일 타임그리드에서 **드래그로 시간 범위를 그어** 만들 때는
     * 그 범위가 곧 사용자의 의도다. 그 경우에만 false 를 넘기면
     * defaultDate/defaultEndDate 의 시각이 그대로 살아난다.
     */
    defaultAllDay: {
      type: Boolean,
      default: true
    }
  },
  emits: ['update:visible', 'event-saved', 'event-deleted'],
  setup(props, { emit }) {
    const store = useStore()
    const { isMobile } = useBreakpoint()
    const formRef = ref(null)
    const loading = ref(false)
    const locations = ref([])
    const locationSearched = ref(false)
    const locationError = ref('')

    // 접힘 상태. 폼 데이터가 아니라 화면 상태라서 form 과 섞지 않는다.
    const recurringOpen = ref(false)
    const locationOpen = ref(false)
    const detailsOpen = ref(false)
    const avatarBroken = ref(false)

    // 범위 선택 시트. 반복 일정을 저장/삭제할 때만 끼어든다.
    const scopeSheetVisible = ref(false)
    const scopeMode = ref('delete')
    const pendingEventData = ref(null)

    const dialogVisible = computed({
      get: () => props.visible,
      set: (value) => emit('update:visible', value)
    })

    const isEdit = computed(() => !!props.event?.id)

    /**
     * 반복 시리즈 식별자. 값이 있으면 이 일정은 펼쳐진 occurrence 중 하나다.
     * 수정으로 들어오는 길은 EventInfoDialog.handleManage 하나이고, 거기서 평평한
     * 객체에 seriesId 를 실어 준다(예전의 schedule./extendedProps. 경로는 호출부가 없다).
     */
    const seriesId = computed(() => props.event?.seriesId || null)

    const canEdit = computed(() => {
      if (!isEdit.value) return true // 새로운 일정 생성은 허용
      // 서버가 판단해 준 값이 있으면 그대로 쓴다(routes/agenda.js — 본인 또는 grade 3 이상).
      if (typeof props.event?.canEdit === 'boolean') return props.event.canEdit

      const currentUser = store.state.user
      if (!currentUser) return false

      // 관리자 또는 중요관리자(grade 3) 이상은 모든 일정 수정 가능
      if (store.getters.isAdmin) return true

      // 본인의 일정만 수정 가능
      return props.event?.userId === currentUser.id
    })

    /**
     * el-form 의 :disabled 는 Element Plus 컨트롤만 잠근다.
     * 종류 칩·행 버튼은 순수 <button> 이라 여기서 따로 잠가야 한다 — 안 그러면
     * 수정 권한이 없는 사람이 칩을 눌러 폼 값이 바뀌고, 저장은 어차피 막히니까
     * 화면만 거짓말을 하는 상태가 된다.
     */
    const isReadonly = computed(() => isEdit.value && !canEdit.value)

    const form = ref({
      title: '',
      description: '',
      startDate: '',
      endDate: '',
      startTime: '',
      endTime: '',
      locationId: null,
      locationCustom: '',
      eventType: 'work',
      isAllDay: true,
      // 서버는 events.color 대신 eventType 에서 색을 파생한다(UnifiedMonthView 주석 참고).
      // 이 값은 사실상 쓰이지 않지만, 남기려면 최소한 앱 팔레트여야 한다 —
      // #409EFF 는 예전 Element Plus 기본 파랑이고 이 앱의 파랑(#2563eb)이 아니다.
      color: '#2563eb',
      isRecurring: false,
      recurringType: '',
      recurringStartDate: '',
      recurringEndDate: '',
      recurringDays: [],
      recurringDayOfMonth: null
    })

    const rules = {
      title: [
        { required: true, message: '제목을 입력해주세요', trigger: 'blur' }
      ],
      startDate: [
        {
          required: true,
          message: '시작일을 선택해주세요',
          trigger: 'change',
          validator: (rule, value, callback) => {
            // 반복 모드가 아닐 때만 startDate 필수
            if (!form.value.isRecurring || isEdit.value) {
              if (!value) {
                callback(new Error('시작일을 선택해주세요'))
              } else {
                callback()
              }
            } else {
              callback()
            }
          }
        }
      ],
      endDate: [
        {
          required: true,
          message: '종료일을 선택해주세요',
          trigger: 'change',
          validator: (rule, value, callback) => {
            // 반복 모드가 아닐 때만 endDate 필수 및 검증
            if (!form.value.isRecurring || isEdit.value) {
              if (!value) {
                callback(new Error('종료일을 선택해주세요'))
                return
              }

              if (value && form.value.startDate) {
                /*
                 * 문자열로 비교한다. 'YYYY-MM-DD HH:mm:ss' 는 고정폭이라 사전순이 곧 시간순이다.
                 * new Date('2026-08-24 09:00:00') 은 iOS Safari 에서 Invalid Date 가 되고,
                 * Invalid Date 끼리의 비교는 늘 false 라 '종료가 시작보다 빠름' 이 그대로 통과했다.
                 */
                const startDate = form.value.startDate
                const endDate = value

                if (form.value.isAllDay) {
                  // 종일 이벤트의 경우 같은 날짜도 허용 — 날짜 부분만 본다
                  if (datePartOf(endDate) < datePartOf(startDate)) {
                    callback(new Error('종료일은 시작일과 같거나 늦어야 합니다'))
                  } else {
                    callback()
                  }
                } else {
                  // 시간 지정 이벤트의 경우 시간까지 고려
                  if (endDate <= startDate) {
                    callback(new Error('종료일은 시작일보다 늦어야 합니다'))
                  } else {
                    callback()
                  }
                }
              } else {
                callback()
              }
            } else {
              callback()
            }
          }
        }
      ],
      recurringType: [
        {
          validator: (rule, value, callback) => {
            if (form.value.isRecurring && !isEdit.value) {
              if (!value) {
                callback(new Error('반복 유형을 선택해주세요'))
              } else {
                callback()
              }
            } else {
              callback()
            }
          },
          trigger: 'change'
        }
      ],
      recurringStartDate: [
        {
          validator: (rule, value, callback) => {
            if (form.value.isRecurring && !isEdit.value) {
              if (!value) {
                callback(new Error('반복 시작 날짜를 선택해주세요'))
              } else {
                callback()
              }
            } else {
              callback()
            }
          },
          trigger: 'change'
        }
      ],
      recurringEndDate: [
        {
          validator: (rule, value, callback) => {
            if (form.value.isRecurring && !isEdit.value) {
              if (!value) {
                callback(new Error('반복 종료 날짜를 선택해주세요'))
              } else if (form.value.recurringStartDate) {
                // 둘 다 'YYYY-MM-DD' 라 문자열 비교로 충분하다(위 endDate 규칙과 같은 이유)
                if (value <= form.value.recurringStartDate) {
                  callback(new Error('반복 종료일은 시작일보다 늦어야 합니다'))
                } else {
                  callback()
                }
              } else {
                callback()
              }
            } else {
              callback()
            }
          },
          trigger: 'change'
        }
      ],
      recurringDays: [
        {
          validator: (rule, value, callback) => {
            if (form.value.isRecurring && !isEdit.value && form.value.recurringType === 'weekly') {
              if (!value || !Array.isArray(value) || value.length === 0) {
                callback(new Error('반복할 요일을 선택해주세요'))
              } else {
                callback()
              }
            } else {
              callback()
            }
          },
          trigger: 'change'
        }
      ],
      recurringDayOfMonth: [
        {
          validator: (rule, value, callback) => {
            if (form.value.isRecurring && !isEdit.value && form.value.recurringType === 'monthly') {
              if (!value || value < 1 || value > 31) {
                callback(new Error('올바른 날짜(1-31)를 입력해주세요'))
              } else {
                callback()
              }
            } else {
              callback()
            }
          },
          trigger: 'change'
        }
      ]
    }

    /* ---------------------------------------------------------------
       날짜 행 / 시간 행이 나눠 쓰는 프록시.
       읽고 쓰는 대상은 여전히 form.startDate · form.endDate 하나씩이다.
       --------------------------------------------------------------- */
    const startDatePart = computed({
      get: () => datePartOf(form.value.startDate),
      set: (v) => {
        if (!v) return
        form.value.startDate = joinDateTime(datePartOf(v), timePartOf(form.value.startDate) || '00:00:00')
      }
    })
    const endDatePart = computed({
      get: () => datePartOf(form.value.endDate),
      set: (v) => {
        if (!v) return
        form.value.endDate = joinDateTime(datePartOf(v), timePartOf(form.value.endDate) || '23:59:59')
      }
    })
    const startTimePart = computed({
      get: () => timePartOf(form.value.startDate),
      set: (v) => {
        if (!v) return
        form.value.startDate = joinDateTime(datePartOf(form.value.startDate), v)
      }
    })
    const endTimePart = computed({
      get: () => timePartOf(form.value.endDate),
      set: (v) => {
        if (!v) return
        form.value.endDate = joinDateTime(datePartOf(form.value.endDate), v)
      }
    })

    /**
     * 시간 pill 은 form.startDate/endDate 의 시간 부분만 바꾼다. 그런데 그 두 필드의
     * el-form-item 은 날짜 행에 있어서 pill 쪽 변경만으로는 검증이 걸리지 않는다.
     * '종료가 시작보다 빠름' 을 저장 버튼 누를 때 처음 알면 늦다.
     */
    function revalidateDates() {
      formRef.value?.validateField(['startDate', 'endDate'])?.catch?.(() => {})
    }

    /* ---------------------------------------------------------------
       접힘 / 요약
       --------------------------------------------------------------- */
    const recurringExpanded = computed(
      () => recurringOpen.value || (form.value.isRecurring && !isEdit.value)
    )

    const recurringSummary = computed(() => {
      if (!form.value.isRecurring) return '반복 안 함'
      if (form.value.recurringType === 'daily') return '매일'
      if (form.value.recurringType === 'weekly') {
        const picked = WEEKDAYS
          .filter((d) => isDaySelected(d.value))
          .map((d) => d.label)
        return picked.length ? `매주 ${picked.join('·')}` : '매주'
      }
      if (form.value.recurringType === 'monthly') {
        return form.value.recurringDayOfMonth
          ? `매월 ${form.value.recurringDayOfMonth}일`
          : '매월'
      }
      return '반복'
    })

    const locationOptional = computed(
      () => form.value.eventType === 'dayoff' || form.value.eventType === 'vacation'
    )

    const selectedLocation = computed(
      () => locations.value.find((l) => l.id === form.value.locationId) || null
    )
    const locationName = computed(() => {
      if (selectedLocation.value) return selectedLocation.value.name
      // 목록이 아직 안 왔는데 locationId 만 있는 순간이 있다 — 빈 줄보다 낫다.
      if (form.value.locationId) return '등록된 장소'
      return form.value.locationCustom ? '직접 입력' : ''
    })
    const locationAddress = computed(
      () => selectedLocation.value?.address || form.value.locationCustom || ''
    )

    // 검증에 걸리면 접혀 있어도 펼쳐야 한다. 안 그러면 저장이 안 되는 이유가 화면에 없다.
    const locationExpanded = computed(() => locationOpen.value || !!locationError.value)

    /* 참석자 필드가 없으므로(Event 모델은 userId 한 명) '누구의 일정인지'만 보여준다.
       새 일정은 나, 수정은 정보 시트가 넘겨 준 소유자(event.owner)다.
       예전에는 남의 일정이면 '다른 사용자' 로만 찍혀, 관리자가 누구 일정을 고치는지 몰랐다. */
    const owner = computed(() => {
      const me = store.state.user
      if (!isEdit.value) return me
      return props.event?.owner || (props.event?.userId === me?.id ? me : null)
    })
    const ownerName = computed(() => owner.value?.nickname || '다른 사용자')
    const ownerAvatar = computed(() =>
      owner.value && !avatarBroken.value ? getProfileImageUrl(owner.value) : null
    )
    const ownerInitial = computed(() => (owner.value ? getProfileInitial(owner.value) : '?'))
    function onAvatarError() {
      avatarBroken.value = true
    }

    function isDaySelected(value) {
      return (form.value.recurringDays || []).some((d) => String(d) === value)
    }
    function isRecurringTypeActive(value) {
      return form.value.isRecurring ? form.value.recurringType === value : value === ''
    }

    function toggleRecurring() {
      if (isReadonly.value || isEdit.value || form.value.isRecurring) return
      recurringOpen.value = !recurringOpen.value
    }
    function toggleLocation() {
      if (isReadonly.value) return
      locationOpen.value = !locationOpen.value
    }

    function selectEventType(value) {
      if (isReadonly.value) return
      form.value.eventType = value
      handleEventTypeChange()
    }

    /**
     * 칩 하나가 isRecurring 과 recurringType 을 동시에 정한다.
     * handleRecurringChange 는 켜짐/꺼짐이 실제로 바뀔 때만 부른다 — 매주↔매월처럼
     * 유형만 바꾸는데도 부르면 기본 시간(09:00~10:00)이 매번 되돌아간다.
     */
    function selectRecurringType(value) {
      if (isReadonly.value || isEdit.value) return
      const next = !!value
      if (next !== form.value.isRecurring) {
        form.value.isRecurring = next
        handleRecurringChange(next)
      }
      form.value.recurringType = value || ''
    }

    function toggleRecurringDay(value) {
      if (isReadonly.value) return
      const list = form.value.recurringDays || []
      const i = list.findIndex((d) => String(d) === value)
      if (i >= 0) list.splice(i, 1)
      else list.push(value)
      form.value.recurringDays = list
      /*
       * el-checkbox-group 은 값이 바뀔 때 자기가 속한 el-form-item 에
       * validate('change') 를 불러 준다(checkbox-group2.mjs). 순수 <button> 칩에는
       * 그 경로가 없어서, "반복할 요일을 선택해주세요" 를 띄운 뒤 요일을 골라도
       * 오류 문구가 다음 저장 시도까지 그대로 남아 있었다.
       */
      formRef.value?.validateField?.('recurringDays')?.catch?.(() => {})
    }

    // 위치 검증 함수
    function validateLocation() {
      // 휴무나 휴가가 아닌 경우 위치 필수
      if (form.value.eventType !== 'dayoff' && form.value.eventType !== 'vacation') {
        // 저장된 위치를 선택했거나, 직접 입력 후 검색한 경우 통과
        if (form.value.locationId || (form.value.locationCustom && locationSearched.value)) {
          locationError.value = ''
          return true
        } else if (form.value.locationCustom && !locationSearched.value) {
          locationError.value = '주소 검색 버튼을 눌러 주소를 확인해주세요'
          return false
        } else {
          locationError.value = '위치를 선택하거나 입력해주세요'
          return false
        }
      } else {
        locationError.value = ''
        return true
      }
    }

    /* 반복 일정의 시각 기본값. 종일이면 의미가 없으니 기존 고정값을 그대로 쓴다. */
    function hhmmss(date, fallback) {
      if (props.defaultAllDay) return fallback
      const p = (n) => String(n).padStart(2, '0')
      return `${p(date.getHours())}:${p(date.getMinutes())}:00`
    }

    function resetForm() {
      // 사본을 만든다. 아래 setHours 가 호출부(Calendar 의 composeStartAt)가 쥔 Date 를
      // 그대로 고쳐 쓰면, 같은 Date 로 다시 열 때 이미 00:00 으로 뭉개진 값이 들어온다.
      const defaultStart = props.defaultDate ? new Date(props.defaultDate) : new Date()
      const defaultEnd = props.defaultEndDate ? new Date(props.defaultEndDate) : new Date(defaultStart)

      /*
       * 종일이 기본일 때만 하루 전체로 편다.
       * 예전에는 이 두 줄이 무조건 실행돼서, 호출부가 정확한 시각을 넘겨도
       * (타임그리드 드래그 10:00~11:00) 00:00~23:59 종일 일정으로 뭉개졌다.
       */
      if (props.defaultAllDay) {
        defaultStart.setHours(0, 0, 0, 0)
        defaultEnd.setHours(23, 59, 59, 999)
      }

      form.value = {
        title: '',
        description: '',
        startDate: toServerDateTime(defaultStart),
        endDate: toServerDateTime(defaultEnd),
        startTime: hhmmss(defaultStart, '13:00:00'),
        endTime: hhmmss(defaultEnd, '18:00:00'),
        locationId: null,
        locationCustom: '',
        eventType: 'work',
        isAllDay: props.defaultAllDay,
        isRecurring: false,
        recurringType: '',
        recurringStartDate: toServerDateTime(defaultStart).slice(0, 10),
        recurringEndDate: '',
        recurringDays: [],
        recurringDayOfMonth: null
      }

      locationSearched.value = false
      locationError.value = ''
    }

    function loadFormData() {
      if (props.event?.id) {
        const event = props.event

        // EventInfoDialog 가 Date 로 넘긴다. toServerDateTime 은 Date·문자열 모두 받는다.
        const startDate = event.startDate
        const endDate = event.endDate

        form.value = {
          title: event.title || '',
          description: event.description || '',
          startDate: toServerDateTime(startDate),
          endDate: toServerDateTime(endDate),
          locationId: event.locationId || null,
          locationCustom: event.locationCustom || '',
          eventType: event.eventType || 'work',
          isAllDay: event.isAllDay !== undefined ? event.isAllDay : false,
          isRecurring: false,
          recurringType: '',
          recurringStartDate: toServerDateTime(startDate).slice(0, 10),
          recurringEndDate: '',
          recurringDays: [],
          recurringDayOfMonth: null
        }

        // 기존 이벤트에 위치 정보가 있으면 검색된 것으로 간주
        if (event.locationId || event.locationCustom) {
          locationSearched.value = true
        } else {
          locationSearched.value = false
        }
        locationError.value = ''
      } else {
        resetForm()
      }
    }

    /*
     * 종일 전환. 날짜는 그대로 두고 시각만 갈아끼운다.
     * 예전에는 new Date(form.startDate) 로 파싱한 뒤 setHours 했는데, 공백 구분 문자열은
     * iOS Safari 에서 Invalid Date 라 아이폰에서 종일 스위치를 끄면 'NaN-NaN-NaN' 이 채워졌다.
     * 문자열의 앞 10글자(날짜)만 쓰면 파싱이 필요 없다.
     */
    function handleAllDayChange(isAllDay) {
      const startDay = datePartOf(form.value.startDate) || toServerDateTime(new Date()).slice(0, 10)
      const endDay = datePartOf(form.value.endDate) || startDay
      form.value.startDate = joinDateTime(startDay, isAllDay ? '00:00:00' : '13:00:00')
      form.value.endDate = joinDateTime(endDay, isAllDay ? '23:59:59' : '18:00:00')
    }

    function handleLocationSelect(locationId) {
      if (locationId) {
        form.value.locationCustom = ''
        locationSearched.value = false
      }
      // 위치 선택 시 에러 클리어
      locationError.value = ''
    }

    function handleLocationInputChange() {
      locationSearched.value = false
      // 입력 변경 시 에러 클리어 (아직 검증하지 않음)
      locationError.value = ''
    }

    function handleRecurringChange(isRecurring) {
      if (!isRecurring) {
        // 반복 설정을 끄면 관련 필드들 초기화
        form.value.recurringType = ''
        form.value.recurringStartDate = ''
        form.value.recurringEndDate = ''
        form.value.recurringDays = []
        form.value.recurringDayOfMonth = null
      } else {
        // 반복 설정을 켜면 기본값 설정
        const defaultStart = props.defaultDate || new Date()
        form.value.recurringStartDate = toServerDateTime(defaultStart).slice(0, 10)
        form.value.startTime = '09:00:00'
        form.value.endTime = '10:00:00'
      }
    }

    function handleEventTypeChange() {
      // 유형 변경 시 에러 클리어 (휴무/휴가로 변경 시 위치 필수 아님)
      locationError.value = ''
    }

    // getEventTypeColor 는 @/utils/eventTypes 에서 import 함

    async function searchAddress() {
      if (!form.value.locationCustom) return

      try {
        const response = await http.post('/api/locations/geocode', {
          address: form.value.locationCustom
        })

        if (response.data.success && response.data.address) {
          // 정확한 주소로 업데이트
          form.value.locationCustom = response.data.address
          locationSearched.value = true
          locationError.value = ''
          ElMessage.success('주소를 찾았습니다.')
        } else {
          ElMessage.warning('주소를 찾을 수 없습니다.')
          locationSearched.value = false
        }
      } catch (error) {
        console.error('주소 검색 오류:', error)
        ElMessage.error('주소 검색에 실패했습니다.')
        locationSearched.value = false
      }
    }

    async function loadLocations() {
      try {
        const response = await store.dispatch('fetchLocations')
        locations.value = response
      } catch (error) {
        console.error('위치 목록 로딩 오류:', error)
      }
    }

    /** 폼 → 서버 페이로드. 저장이 두 갈래(즉시 / 범위 선택 후)가 되면서 분리했다. */
    function buildEventData() {
      // 기본 이벤트 데이터 생성
      let startDate, endDate

      if (!isEdit.value && form.value.isRecurring) {
        // 반복 모드: recurringStartDate와 시간 정보로 startDate, endDate 생성
        if (form.value.isAllDay) {
          startDate = form.value.recurringStartDate + ' 00:00:00'
          endDate = form.value.recurringStartDate + ' 23:59:59'
        } else {
          startDate = form.value.recurringStartDate + ' ' + (form.value.startTime || '13:00:00')
          endDate = form.value.recurringStartDate + ' ' + (form.value.endTime || '14:00:00')
        }
      } else {
        // 일반 모드: 기존 startDate, endDate 사용
        startDate = form.value.startDate
        endDate = form.value.endDate
      }

      const eventData = {
        title: form.value.title,
        description: form.value.description,
        startDate: startDate,
        endDate: endDate,
        locationId: form.value.locationId || null,
        locationCustom: form.value.locationCustom || null,
        eventType: form.value.eventType,
        isAllDay: form.value.isAllDay,
        color: getEventTypeColor(form.value.eventType)
      }

      // 반복 설정 추가 (생성 시에만)
      if (!isEdit.value && form.value.isRecurring) {
        eventData.isRecurring = true
        eventData.recurringType = form.value.recurringType
        eventData.recurringStartDate = form.value.recurringStartDate
        eventData.recurringEndDate = form.value.recurringEndDate

        // 반복 일정의 경우 시간 정보 설정
        if (!form.value.isAllDay) {
          eventData.startTime = form.value.startTime
          eventData.endTime = form.value.endTime
        }

        if (form.value.recurringType === 'weekly') {
          eventData.recurringDays = form.value.recurringDays
        } else if (form.value.recurringType === 'monthly') {
          eventData.recurringDayOfMonth = form.value.recurringDayOfMonth
        }
      }

      return eventData
    }

    /** scope 는 반복 시리즈일 때만 값이 있다. null 이면 서버 기본값(this)으로 1건만 처리된다. */
    async function persistEvent(eventData, scope) {
      loading.value = true
      try {
        if (isEdit.value) {
          const result = await store.dispatch('updateEvent', {
            id: props.event.id,
            eventData,
            scope
          })
          ElMessage.success(updateSuccessMessage(scope, result))
        } else {
          const result = await store.dispatch('createEvent', eventData)

          // 반복 일정 생성 결과에 따른 메시지 표시
          if (result && result.count) {
            ElMessage.success(`${result.count}개의 반복 일정이 생성되었습니다.`)
          } else {
            ElMessage.success('일정이 추가되었습니다.')
          }
        }

        emit('event-saved')
        /*
         * 성공하면 닫는다.
         *
         * 예전에는 emit 만 하고 끝나서 저장이 됐는데도 폼이 그대로 남아 있었다.
         * 부모(Calendar.vue)는 @event-saved 로 reload 만 하지 닫지 않는다 —
         * 여는 쪽이 여러 곳(FAB · 일자 시트 · 상단바 · 타임그리드 드래그)이라
         * 닫는 책임을 부모에 두면 한 곳만 빠져도 이 증상이 다시 난다.
         * 실패했을 때는 닫지 않는다. 입력한 내용을 잃으면 안 된다.
         */
        dialogVisible.value = false
      } catch (error) {
        console.error('일정 저장 오류:', error)
        ElMessage.error(
          error.response?.data?.message ||
            (isEdit.value ? '일정 수정에 실패했습니다.' : '일정 추가에 실패했습니다.')
        )
      } finally {
        loading.value = false
      }
    }

    /**
     * 몇 건이 바뀌었는지 알려준다. 20건짜리 시리즈를 고쳤는데 '일정이 수정되었습니다'
     * 하나만 뜨면 반영됐는지 확인하러 캘린더를 훑게 된다.
     *
     * updateEvent 액션은 지금 response.data.event 만 돌려줘서 updated 가 대개 없다.
     * 액션이 응답 봉투를 그대로 넘기게 되면 건수 문구가 저절로 나오고,
     * 그전까지는 범위에 맞는 문구로 대신한다.
     */
    function updateSuccessMessage(scope, result) {
      const count = Number(result?.updated) || 0
      if (count > 1) return `${count}개의 일정이 수정되었습니다.`
      if (scope === 'thisAndFuture') return '이 일정 및 이후 반복이 수정되었습니다.'
      if (scope === 'all') return '전체 반복 일정이 수정되었습니다.'
      return '일정이 수정되었습니다.'
    }

    async function handleSubmit() {
      // 저장 중 두 번째 탭을 막는다. loading 은 persistEvent 안에서야 켜지는데 그 전에
      // validate() 를 await 하는 틈이 있어, 빠른 더블 탭이면 같은 일정이 두 건 만들어졌다.
      if (!formRef.value || loading.value) return
      loading.value = true
      try {
        await formRef.value.validate()
      } catch {
        loading.value = false
        // 폼 검증 실패는 각 필드에 이미 표시된다. 토스트까지 띄우면 중복이다.
        // 접힘 뒤로 숨는 필드에는 규칙을 두지 않았다 — 반복 상세는 켜져 있는 동안
        // 펼친 채 고정이고, '자세히'(설명)에는 검증 규칙이 없다.
        return
      }

      loading.value = false

      // 위치 검증 (수동)
      if (!validateLocation()) {
        return
      }

      const eventData = buildEventData()

      // 반복 시리즈일 때만 범위를 묻는다. 단발 일정에 없던 확인 단계를 만들지 않는다.
      if (isEdit.value && seriesId.value) {
        pendingEventData.value = eventData
        scopeMode.value = 'edit'
        scopeSheetVisible.value = true
        return
      }

      await persistEvent(eventData, null)
    }

    async function performDelete(scope) {
      loading.value = true
      try {
        const result = await store.dispatch('deleteEvent', { id: props.event.id, scope })
        const count = Number(result?.deleted) || 1
        ElMessage.success(
          count > 1 ? `${count}개의 일정이 삭제되었습니다.` : '일정이 삭제되었습니다.'
        )
        emit('event-deleted')
        dialogVisible.value = false
      } catch (error) {
        console.error('일정 삭제 오류:', error)
        ElMessage.error(error.response?.data?.message || '일정 삭제에 실패했습니다.')
      } finally {
        loading.value = false
      }
    }

    async function handleDelete() {
      // 반복이면 범위 선택 시트가 확인 단계를 겸한다 — 확인을 두 번 받지 않는다.
      if (seriesId.value) {
        scopeMode.value = 'delete'
        scopeSheetVisible.value = true
        return
      }

      try {
        await ElMessageBox.confirm(
          '이 일정을 삭제하시겠습니까?',
          '삭제 확인',
          {
            confirmButtonText: '삭제',
            cancelButtonText: '취소',
            type: 'warning'
          }
        )
      } catch {
        return // 취소
      }

      await performDelete(null)
    }

    function handleScopeConfirm(scope) {
      // 시트를 닫는 건 렌더 사이클 뒤라 그 사이 두 번째 탭이 들어올 수 있다.
      // '전체 반복 삭제' 를 두 번 보내면 두 번째는 deleted=0 인데도 성공 토스트가 뜬다.
      if (loading.value) return

      scopeSheetVisible.value = false

      if (scopeMode.value === 'delete') {
        performDelete(scope)
        return
      }

      const eventData = pendingEventData.value
      pendingEventData.value = null
      if (eventData) persistEvent(eventData, scope)
    }

    function handleClose() {
      dialogVisible.value = false
    }

    /**
     * 열릴 때마다: 폼 채우기 · 장소 목록 · 접힘 상태 초기화.
     *
     * 장소 목록은 **열 때만** 부른다. 예전에는 onMounted 에서도 불러서, 이 다이얼로그를
     * 품은 화면(캘린더 · 홈의 정보 시트)이 뜨기만 해도 열지도 않을 장소 목록을 받아 왔다.
     *
     * 접힘은 장소만 예외적으로 '비어 있으면 펼친 채' 시작한다 — 휴무·휴가가 아닌 일정에서
     * 장소는 필수인데, 필수 필드를 탭 한 번 뒤에 숨겨 두면 저장을 누른 뒤에야
     * 그런 필드가 있었다는 걸 알게 된다.
     *
     * immediate 인 이유: 상단바 '새로 만들기 › 일정' 으로 캘린더에 **처음** 들어오면
     * Calendar 가 setup 중에 이미 visible=true 로 연 채 이 컴포넌트를 마운트한다.
     * 값이 바뀐 적이 없으니 일반 watch 는 불리지 않고, 날짜 칸이 빈 폼이 열렸다.
     */
    watch(() => props.visible, (visible) => {
      if (!visible) return
      loading.value = false
      loadFormData()
      loadLocations()
      recurringOpen.value = false
      detailsOpen.value = false
      avatarBroken.value = false
      locationOpen.value = !(props.event?.locationId || props.event?.locationCustom)
    }, { immediate: true })

    // 위치 검증에 걸리면 접혀 있어도 펼친다(locationExpanded 가 error 를 함께 본다).
    watch(locationError, (message) => {
      if (message) locationOpen.value = true
    })

    return {
      dialogVisible,
      isEdit,
      canEdit,
      isReadonly,
      form,
      rules,
      formRef,
      loading,
      locations,
      locationSearched,
      locationError,
      isMobile,
      scopeSheetVisible,
      scopeMode,
      handleScopeConfirm,
      eventTypeOptions: EVENT_TYPE_LIST,
      recurringOptions: RECURRING_OPTIONS,
      weekdays: WEEKDAYS,
      startDatePart,
      endDatePart,
      startTimePart,
      endTimePart,
      revalidateDates,
      recurringOpen,
      recurringExpanded,
      recurringSummary,
      locationOpen,
      locationExpanded,
      locationOptional,
      locationName,
      locationAddress,
      detailsOpen,
      ownerName,
      ownerAvatar,
      ownerInitial,
      onAvatarError,
      isDaySelected,
      isRecurringTypeActive,
      toggleRecurring,
      toggleLocation,
      selectEventType,
      selectRecurringType,
      toggleRecurringDay,
      handleAllDayChange,
      handleLocationSelect,
      handleLocationInputChange,
      handleRecurringChange,
      handleEventTypeChange,
      searchAddress,
      handleSubmit,
      handleDelete,
      handleClose,
      Search,
      Delete,
      CircleCheck
    }
  }
}
</script>

<style scoped>
.ed {
  min-width: 0;
}

/* ------------------------------------------------------------------
   종류 칩
   40px 은 폰에서 손가락이 닿는 최소선(--tap-min)에 맞춘 값이다. 6개가 한 줄에
   안 들어가면 가로로 흐르게 둔다 — 줄바꿈해서 두 줄이 되면 그 아래 제목까지
   밀려 내려가 첫 화면에서 제목이 사라진다.
   ------------------------------------------------------------------ */
.ed-types {
  display: flex;
  gap: var(--sp-2);
  overflow-x: auto;
  padding-bottom: var(--sp-1);
  margin-bottom: var(--sp-4);
  /* 스크롤바를 지웠으므로 오른쪽 끝을 페이드시켜 "더 있다" 를 알린다.
     390px 에서 칩 6개는 406px 이라 '휴가' 가 21px 만 노출된다. */
  -webkit-mask-image: linear-gradient(to right, #000 calc(100% - 24px), transparent);
  mask-image: linear-gradient(to right, #000 calc(100% - 24px), transparent);
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}

.ed-types::-webkit-scrollbar { display: none; }

.ed-type {
  --evt-c: var(--color-text-muted);
  position: relative;
  flex: 0 0 auto;
  height: 40px;
  padding: 0 var(--sp-4);
  border: 1px solid var(--color-border);
  border-radius: var(--r-full);
  background: var(--color-surface);
  color: var(--color-text-muted);
  /* 버튼은 UA 스타일시트가 자체 글꼴을 강제한다. inherit 안 하면 이 줄만 다른 폰트가 된다. */
  font-family: inherit;
  font-size: var(--fs-md);
  font-weight: var(--fw-medium);
  white-space: nowrap;
  cursor: pointer;
  transition: border-color var(--dur-fast) var(--ease), background var(--dur-fast) var(--ease);
}

/* 칩 자체는 40px 이지만 히트 영역만 위아래 2px 씩 넓혀 --tap-min(44px)을 만든다.
   ::after 는 흐름 밖이라 줄 높이는 그대로다(TaskDialog.vue 와 같은 수법).
   좌우로는 넓히지 않는다 — 칩 사이 간격이 8px 뿐이라 옆 칩을 덮는다. */
.ed-type::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: -2px;
  bottom: -2px;
}

.ed-type:disabled { cursor: default; opacity: 0.55; }
.ed-type:active:not(:disabled) { background: var(--color-surface-2); }

.ed-type--work { --evt-c: var(--evt-work); }
.ed-type--substitute { --evt-c: var(--evt-substitute); }
.ed-type--fieldwork { --evt-c: var(--evt-fieldwork); }
.ed-type--inspection { --evt-c: var(--evt-inspection); }
.ed-type--dayoff { --evt-c: var(--evt-dayoff); }
.ed-type--vacation { --evt-c: var(--evt-vacation); }

/*
 * 선택된 칩만 종류색을 입는다. 여섯 개가 전부 제 색으로 켜져 있으면 무엇을 고른
 * 상태인지 한눈에 안 잡힌다 — 예전 el-select 에서 색이 아예 없던 것과 반대 방향의 실패다.
 * 글자색은 종류색 원본이 아니라 잉크 쪽으로 한 번 당겨 쓴다: --evt-substitute(#16a34a)를
 * 옅은 배경 위에 그대로 올리면 3:1 언저리라 본문 대비에 미달한다.
 * color-mix 를 모르는 브라우저는 앞줄(surface-2 / 종류색 원본)로 떨어진다.
 */
.ed-type.is-active {
  border-color: var(--evt-c);
  font-weight: var(--fw-semibold);
  background: var(--color-surface-2);
  background: color-mix(in srgb, var(--evt-c) 10%, var(--color-surface));
  color: var(--evt-c);
  color: color-mix(in srgb, var(--evt-c) 72%, var(--color-text));
}

/* ------------------------------------------------------------------
   제목 — 라벨을 없애고 입력 자체를 제목처럼 크게. placeholder 가 라벨 역할을 한다.
   ------------------------------------------------------------------ */
/* margin 은 아래 `.ed :deep(.el-form-item)` 초기화보다 특이도가 높아야 한다.
   .ed-item 과 .ed-item--title 을 함께 걸어 그 한 줄만 예외로 남긴다. */
.ed .ed-item.ed-item--title { margin-bottom: var(--sp-4); }

.ed-title :deep(.el-input__wrapper) {
  padding: var(--sp-2) 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  border-bottom: 2px solid var(--color-border-strong);
  transition: border-color var(--dur-fast) var(--ease);
}

.ed-title :deep(.el-input__wrapper.is-focus),
.ed-title :deep(.el-input__wrapper:hover) { box-shadow: none; }

.ed-title:focus-within :deep(.el-input__wrapper) { border-bottom-color: var(--color-primary); }

.ed-title :deep(.el-input__inner) {
  height: 36px;
  font-size: var(--fs-title);
  font-weight: var(--fw-semibold);
  letter-spacing: -0.02em;
  color: var(--color-text);
}

.ed-title :deep(.el-input__inner::placeholder) {
  font-weight: var(--fw-regular);
  color: var(--color-text-subtle);
}

/* ------------------------------------------------------------------
   카드 · 행
   ------------------------------------------------------------------ */
.ed-card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--r-lg);
  box-shadow: var(--sh-1);
  margin-bottom: var(--sp-3);
  overflow: hidden;
}

.ed-row {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  width: 100%;
  min-height: 52px;
  padding: var(--sp-2) var(--sp-4);
  border-top: 1px solid var(--color-border);
  text-align: left;
}

.ed-card > .ed-row:first-child { border-top: 0; }

/* 행 전체가 탭 대상인 경우. <button> 기본 스타일을 지우고 행 모양만 남긴다. */
.ed-rowbtn {
  border-left: 0;
  border-right: 0;
  border-bottom: 0;
  background: none;
  font-family: inherit;
  font-size: var(--fs-md);
  color: var(--color-text);
  cursor: pointer;
}

.ed-rowbtn:active:not(:disabled) { background: var(--color-surface-2); }
.ed-rowbtn:disabled { cursor: default; }

.ed-row__ico {
  flex-shrink: 0;
  font-size: 16px;
  color: var(--color-text-subtle);
}

.ed-row__label {
  flex-shrink: 0;
  font-size: var(--fs-md);
  color: var(--color-text-muted);
}

.ed-req { margin-left: 2px; color: var(--color-danger); }

.ed-row__val {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: var(--sp-1);
  margin-left: auto;
  min-width: 0;
}

.ed-row__summary {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1px;
  margin-left: auto;
  min-width: 0;
  max-width: 62%;
  font-size: var(--fs-md);
  color: var(--color-text);
  text-align: right;
}

/* u-trunc 는 min-width:0 만 준다. 부모가 column flex 라 폭이 콘텐츠 기준으로
   잡히므로, max-width 를 걸지 않으면 긴 설명이 잘리지 않고 행을 밀어낸다. */
.ed-row__summary > span { max-width: 100%; }

.is-muted { color: var(--color-text-subtle); }

.ed-loc__name {
  max-width: 100%;
  font-weight: var(--fw-semibold);
  color: var(--color-text);
}

.ed-loc__addr {
  max-width: 100%;
  font-size: var(--fs-xs);
  color: var(--color-text-subtle);
}

.ed-row__chev {
  flex-shrink: 0;
  color: var(--color-text-subtle);
  transition: transform var(--dur) var(--ease);
}

.ed-row__chev.is-open { transform: rotate(180deg); }

.ed-tilde {
  flex-shrink: 0;
  color: var(--color-text-subtle);
}

/* 펼쳐진 상세. 카드 안에서 한 단계 눌린 영역이라 surface-3. */
.ed-sub {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  padding: var(--sp-3) var(--sp-4) var(--sp-4);
  border-top: 1px solid var(--color-border);
  background: var(--color-surface-3);
}

.ed-subgrid {
  display: flex;
  gap: var(--sp-3);
}

.ed-subgrid > :deep(.el-form-item) { flex: 1 1 0; min-width: 0; }

/* ------------------------------------------------------------------
   el-form-item 을 행 안에 넣기 위한 정리.
   기본 오류 메시지는 position:absolute 라 52px 행 위에 겹쳐 뜬다.
   static 으로 내려 행이 잠깐 두꺼워지는 쪽을 택했다 — 겹쳐서 안 읽히는 것보다 낫다.
   ------------------------------------------------------------------ */
.ed :deep(.el-form-item) { margin-bottom: 0; }

.ed :deep(.el-form-item__error) {
  position: static;
  flex: 0 0 100%;
  padding-top: var(--sp-1);
  font-size: var(--fs-xs);
  line-height: 1.4;
}

/*
 * 인라인 폼 아이템(날짜·시간 픽커)의 오류 문구.
 *
 * el-form-item__content 는 EP 기본이 flex-wrap:wrap 이라 위의 `flex: 0 0 100%` 가
 * **픽커 폭(104px) 기준 100%** 로 잡힌다. "종료일은 시작일보다 늦어야 합니다" 가
 * 104px 칸에서 서너 줄로 접히고, .ed-row__val 이 wrap 이라 시작/종료 픽커가
 * 위아래로 갈라지면서 '~' 가 붕 뜬다.
 * 오류만 행 폭으로 빼내고, 뜰 자리는 행 아래 여백으로 미리 예약한다.
 */
.ed-item--inline { width: auto; min-width: 0; }
.ed-item--inline :deep(.el-form-item__content) { flex-wrap: nowrap; }
.ed-item--inline :deep(.el-form-item__error) {
  position: absolute;
  left: var(--sp-4);
  right: var(--sp-4);
  top: auto;
  bottom: var(--sp-1);
  padding-top: 0;
  text-align: right;
  white-space: normal;
}

/* 오류가 절대배치로 앉을 기준. 날짜·시간 행에만 붙인다. */
.ed-row--dt { position: relative; padding-bottom: var(--sp-5); }

.ed-sub .field-label {
  flex: 0 0 100%;
  margin-bottom: var(--sp-1);
}

/* ------------------------------------------------------------------
   날짜 · 시간 컨트롤
   base.css 가 다이얼로그 안의 모든 el-date-editor 를 width:100% 로 눕힌다.
   행 오른쪽에 값처럼 붙여야 하므로 여기서만 되돌린다.
   ------------------------------------------------------------------ */
.ed :deep(.ed-date.el-date-editor) { width: 104px; }
.ed :deep(.ed-time.el-date-editor) { width: 68px; }

/* 픽커 팝오버의 트리거는 wrapper 다. inner(32px)를 키워 봐야 글자만 커지고
   히트 영역은 그대로라, 위아래 여백으로 --tap-min(44px)을 만든다. */
.ed :deep(.ed-date .el-input__wrapper),
.ed :deep(.ed-time .el-input__wrapper) {
  padding: 6px var(--sp-1);
  background: transparent;
  box-shadow: none;
}

/* 행 왼쪽에 이미 아이콘이 있다. 픽커 안의 달력/시계 아이콘까지 두면 아이콘이 둘이다. */
.ed :deep(.ed-date .el-input__prefix),
.ed :deep(.ed-time .el-input__prefix) { display: none; }

.ed :deep(.ed-date .el-input__inner),
.ed :deep(.ed-time .el-input__inner) {
  height: 32px;
  font-family: var(--font-num);
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';
  font-size: var(--fs-md);
  font-weight: var(--fw-medium);
  color: var(--color-text);
  cursor: pointer;
}

.ed :deep(.ed-date .el-input__inner) { text-align: right; }
.ed :deep(.ed-time .el-input__inner) { text-align: center; }

/*
 * 시간은 pill 두 개다. 지금 열어 둔 쪽만 파랗게 — '시작'과 '종료'가 나란한데
 * 어느 시계를 띄운 건지 표시가 없으면 엉뚱한 쪽을 고치고 나간다.
 */
.ed-pill {
  position: relative;
  display: inline-flex;
  align-items: center;
  height: 36px;
  padding: 0 var(--sp-1);
  border: 1px solid var(--color-border);
  border-radius: var(--r-full);
  background: var(--color-surface-2);
  transition: background var(--dur-fast) var(--ease), border-color var(--dur-fast) var(--ease);
}

/* 36px 은 44px 까지 8px 이 모자라므로 위아래 4px 씩. 좌우는 '~' 간격이 4px 뿐이라
   넓히면 옆 pill 을 덮는다. */
.ed-pill::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: -4px;
  bottom: -4px;
}

.ed-pill:focus-within {
  border-color: var(--color-primary);
  background: var(--color-primary-soft);
}

/* ------------------------------------------------------------------
   반복 상세 칩
   ------------------------------------------------------------------ */
.ed-chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-2);
  width: 100%;
}

.ed-chip {
  position: relative;
  height: 40px;
  padding: 0 var(--sp-3);
  border: 1px solid var(--color-border);
  border-radius: var(--r-full);
  background: var(--color-surface);
  color: var(--color-text-muted);
  font-family: inherit;
  font-size: var(--fs-md);
  white-space: nowrap;
  cursor: pointer;
}

.ed-chip::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: -2px;
  bottom: -2px;
}

.ed-chip:disabled { cursor: default; opacity: 0.55; }
.ed-chip:active:not(:disabled) { background: var(--color-surface-2); }

.ed-chip.is-active {
  border-color: var(--color-primary);
  background: var(--color-primary-soft);
  color: var(--color-primary-strong);
  font-weight: var(--fw-semibold);
}

/*
 * 요일은 7개가 항상 한 줄에 들어와야 한다. 390px 에서 44px 고정으로 두면
 * 두 줄로 접히면서 '월~금' 을 고르는 데 두 번 훑어야 한다. 폭은 나눠 갖고
 * 탭 높이(44px)만 지킨다.
 */
.ed-chips--days { gap: var(--sp-1); flex-wrap: nowrap; }

.ed-chip--day {
  flex: 1 1 0;
  min-width: 0;
  max-width: 56px;
  height: var(--tap-min);
  padding: 0;
  text-align: center;
}

.ed :deep(.ed-num) { width: 132px; }
.ed :deep(.ed-full) { width: 100%; }

/* ------------------------------------------------------------------
   보조 문구
   ------------------------------------------------------------------ */
.ed-note {
  display: flex;
  align-items: center;
  gap: var(--sp-1);
  margin: 0;
  font-size: var(--fs-xs);
  line-height: 1.5;
  color: var(--color-text-muted);
}

.ed-note--ok { color: var(--color-success-strong); }
.ed-note--warn { color: var(--color-warning-strong); }
.ed-note--err { color: var(--color-danger); }

/* ------------------------------------------------------------------
   담당
   ------------------------------------------------------------------ */
.ed-owner { gap: var(--sp-2); }

.ed-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border-radius: var(--r-full);
  overflow: hidden;
  background: var(--color-primary-soft);
  color: var(--color-primary-strong);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
}

.ed-avatar img { width: 100%; height: 100%; object-fit: cover; }

.ed-owner__name {
  max-width: 180px;
  font-size: var(--fs-md);
  color: var(--color-text);
}

.ed-nopermit { margin: var(--sp-4) 0 0; }

/* ------------------------------------------------------------------
   푸터
   ------------------------------------------------------------------ */
.ed-footer {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  width: 100%;
}

.ed-footer__main {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin-left: auto;
}

/* Element Plus 는 인접 버튼에 margin-left 를 넣는다. gap 과 겹쳐 간격이 두 배가 된다. */
.ed-footer :deep(.el-button + .el-button) { margin-left: 0; }

.ed-del__text { margin-left: var(--sp-1); }

@media (max-width: 767px) { /* --bp-mobile-max */
  /* 주 버튼은 엄지 반경에서 풀폭 52px. 삭제는 텍스트를 접고 정사각 아이콘 버튼으로. */
  .ed-footer__main {
    flex: 1;
    margin-left: 0;
  }

  .ed-footer__main :deep(.el-button) {
    flex: 1;
    width: 100%;
    height: var(--tap-lg);
    font-size: var(--fs-md);
  }

  .ed-del {
    width: var(--tap-lg);
    height: var(--tap-lg);
    padding: 0;
    flex-shrink: 0;
  }

  .ed-del__text { display: none; }

  .ed-row { padding: var(--sp-2) var(--sp-3); }
  .ed-sub { padding: var(--sp-3); }
  .ed-row__summary { max-width: 58%; }
}
</style>
